export interface FloatingDropdownRect {
  top: number;
  bottom: number;
  left: number;
}

export interface FloatingDropdownPosition {
  top: number;
  left: number;
  maxHeight: number;
  maxWidth: number;
  openUpward: boolean;
}

interface CalculateFloatingDropdownPositionArgs {
  anchorRect: FloatingDropdownRect;
  dropdownWidth: number;
  dropdownHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  gap?: number;
  margin?: number;
}

/**
 * Places a fixed dropdown on the side of its anchor with enough room, falling
 * back to the roomier side and constraining the dropdown to the viewport.
 */
export function calculateFloatingDropdownPosition({
  anchorRect,
  dropdownWidth,
  dropdownHeight,
  viewportWidth,
  viewportHeight,
  gap = 4,
  margin = 8,
}: CalculateFloatingDropdownPositionArgs): FloatingDropdownPosition {
  const maxWidth = Math.max(0, viewportWidth - margin * 2);
  const renderedWidth = Math.min(dropdownWidth, maxWidth);
  const spaceBelow = Math.max(
    0,
    viewportHeight - anchorRect.bottom - gap - margin
  );
  const spaceAbove = Math.max(0, anchorRect.top - gap - margin);
  const fitsBelow = dropdownHeight <= spaceBelow;
  const fitsAbove = dropdownHeight <= spaceAbove;
  const openUpward = !fitsBelow && (fitsAbove || spaceAbove > spaceBelow);
  const maxHeight = Math.min(
    openUpward ? spaceAbove : spaceBelow,
    Math.max(0, viewportHeight - margin * 2)
  );
  const renderedHeight = Math.min(dropdownHeight, maxHeight);
  const maxLeft = Math.max(margin, viewportWidth - margin - renderedWidth);
  const left = Math.min(Math.max(anchorRect.left, margin), maxLeft);
  const preferredTop = openUpward
    ? anchorRect.top - gap - renderedHeight
    : anchorRect.bottom + gap;
  const maxTop = Math.max(margin, viewportHeight - margin - renderedHeight);
  const top = Math.min(Math.max(preferredTop, margin), maxTop);

  return {
    top,
    left,
    maxHeight,
    maxWidth,
    openUpward,
  };
}
